import { type RoleType } from '../../models/Role'
import { type BuyAndPromoteResponse, type SeatPurchase } from '../../store/auth/auth'
import { readPromoteResult } from './promoteResult'

export interface BuyVerdict {
  ok: boolean
  bought: boolean
  message?: string
  closesModal: boolean
}

export interface BatchBuyVerdict {
  ok: boolean
  sent: number
  total: number
  bought: boolean
  purchased: SeatPurchase
  message?: string
  closesModal: boolean
}

const NOTHING_PURCHASED: SeatPurchase = { general: 0, sidecar: 0 }

const UNKNOWN = 'We could not confirm the result. Reopen this window to see the current seats and requests.'
const PAYMENT_METHOD_MISSING = 'A payment method is needed before you can buy a seat. You can add one on the Plan page.'
const BOUGHT_NOT_SENT = 'The seat was purchased, but the invitation was not sent. The seat stays on your plan, and you can assign it from this list.'
const PURCHASE_FAILED = 'The purchase did not go through, so the invitation was not sent. The request is unchanged.'
const SEATS_STAY = 'The seats you bought stay on your plan, and you can assign them from the Requested list.'
const REST_UNCHANGED = 'The rest are unchanged in the Requested list.'
const NONE_SENT_BOUGHT = `No invitation was sent. ${SEATS_STAY}`
const NONE_SENT = 'The purchase did not go through, so no invitation was sent. The requests are unchanged.'

export const seatsBought = (purchased: SeatPurchase | undefined): boolean => (purchased?.general ?? 0) + (purchased?.sidecar ?? 0) > 0

export const readBuyResult = (response: BuyAndPromoteResponse | undefined, invitationId: string, seatType: RoleType): BuyVerdict => {
  if (!response) {
    return { ok: false, bought: false, message: UNKNOWN, closesModal: false }
  }

  if (response.checkoutRequired) {
    return { ok: false, bought: false, message: PAYMENT_METHOD_MISSING, closesModal: false }
  }

  const bought = seatsBought(response.purchased)
  const verdict = readPromoteResult(response, invitationId, seatType)

  if (verdict.ok) {
    return { ok: true, bought, closesModal: true }
  }

  if (bought) {
    return { ok: false, bought, message: BOUGHT_NOT_SENT, closesModal: false }
  }

  if (verdict.closesModal) {
    return { ok: false, bought, message: verdict.message, closesModal: true }
  }

  return { ok: false, bought, message: PURCHASE_FAILED, closesModal: false }
}

export const readBatchBuyResult = (response: BuyAndPromoteResponse | undefined, invitationIds: string[]): BatchBuyVerdict => {
  const total = invitationIds.length

  if (!response) {
    return { ok: false, sent: 0, total, bought: false, purchased: NOTHING_PURCHASED, message: UNKNOWN, closesModal: false }
  }

  const purchased = response.purchased ?? NOTHING_PURCHASED
  const bought = seatsBought(purchased)

  if (response.checkoutRequired) {
    return { ok: false, sent: 0, total, bought, purchased, message: bought ? NONE_SENT_BOUGHT : PAYMENT_METHOD_MISSING, closesModal: false }
  }

  const sent = response.results.filter((result) => result.outcome === 'promoted').length

  if (total > 0 && sent === total) {
    return { ok: true, sent, total, bought, purchased, closesModal: true }
  }

  if (sent > 0) {
    return {
      ok: false,
      sent,
      total,
      bought,
      purchased,
      message: `${sent} of ${total} invitations were sent. ${bought ? SEATS_STAY : REST_UNCHANGED}`,
      closesModal: false,
    }
  }

  return { ok: false, sent, total, bought, purchased, message: bought ? NONE_SENT_BOUGHT : NONE_SENT, closesModal: false }
}
